"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { TOOLS } from "@suite/lib/tools";
import { cn } from "@suite/lib/utils";

// useLayoutEffect warns during SSR; fall back to useEffect on the server (never runs there).
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Sticky tool bar rendered directly under the fixed marketing header. This release lists only
// Browser Fingerprint; the rest of the suite remains off the staging and production surfaces.
// z-20 keeps it under the marketing shell's z-30 vertical lines so they stay visible
// crossing the bar; page content below is unlayered, so the bar still covers it on scroll.
export default function ToolsSubNav() {
  const pathname = usePathname();

  // Next.js scrolls the *changed segment* into view, and this segment starts with the fixed
  // header's spacer — so arriving here lands ~30px down the page instead of at the top. The
  // tool links disable Next's scroll (scroll={false}) and this layout effect resets to the true
  // top BEFORE the new page paints, so there is no visible jump.
  //
  // prevPath starts null rather than at the current path, so this also runs on first mount:
  // that is the /tools → /tools/fingerprint case, which used to arrive scrolled down because
  // the effect only compared against a path it had already been initialised with.
  const prevPath = useRef<string | null>(null);
  useIsomorphicLayoutEffect(() => {
    if (prevPath.current === pathname) return;
    prevPath.current = pathname;
    // A hash means the visitor asked for a specific place on the page (the report's own
    // "see all …" jump links, or a shared deep link) — never fight that.
    if (window.location.hash) return;
    window.scrollTo(0, 0);
    // After this commit, Next focuses the new page's <main> for accessibility. That element
    // starts below the fixed header, so the browser scrolls it into view honouring the global
    // scroll-padding-top (80px) and lands ~30px down — which is why the reset above alone was
    // not enough. Re-assert the top on the next frame, which still runs before paint.
    const frame = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(frame);
  }, [pathname]);

  return (
    <nav
      aria-label="Free tools"
      className="border-border bg-background/95 sticky top-(--header-height) z-20 border-b backdrop-blur-md"
    >
      {/* px is item padding (px-3) short of the site's px-4/6/8 so the first label aligns
          flush with the section content below. */}
      <div className="scrollbar-hide flex items-center overflow-x-auto px-1 sm:px-3 lg:px-5">
        {TOOLS.map(({ icon: Icon, label, href }) => {
          const active = pathname === href || pathname === `${href}/`;
          return (
            <Link
              key={href}
              href={href}
              scroll={false}
              aria-current={active ? "page" : undefined}
              className={cn(
                "relative flex shrink-0 items-center gap-1.5 px-3 py-3 text-[13px] font-medium whitespace-nowrap transition-colors",
                active
                  ? "text-foreground after:bg-brand-green after:absolute after:inset-x-3 after:bottom-0 after:h-0.5"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className={cn("size-3.5", active && "text-brand-green")} aria-hidden />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
